import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, FileText, Paperclip, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import api, { formatApiError } from "../lib/api";
import { money } from "../lib/format";

export function PaymentGroups({ onChanged }) {
  const [payments, setPayments] = useState([]);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ note: "", ach_reference: "", file: null });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payments");
      setPayments(data.payments);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPay = (payment) => {
    setTarget(payment);
    setForm({ note: "", ach_reference: payment.ach_reference || "", file: null });
  };

  const markPaid = async () => {
    if (!form.file && !window.confirm(
      "No bank confirmation report is attached. The notification email will go out without it. Continue?")) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("note", form.note);
      fd.append("ach_reference", form.ach_reference);
      if (form.file) fd.append("file", form.file);
      const { data } = await api.post(`/admin/payments/${target.id}/mark-paid`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Payment settled · ${data.notified_count} recipient(s) emailed`
        + (data.confirmation_attached ? " with the confirmation report" : ""));
      setTarget(null);
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const ungroup = async (payment) => {
    if (!window.confirm(`Ungroup the ${payment.vendor_name} payment? The invoices return to the approved queue.`)) return;
    try {
      await api.delete(`/admin/payments/${payment.id}`);
      toast.success("Payment ungrouped");
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="payment-groups">
      <p className="text-sm mb-6" style={{ color: "var(--muted)" }}>
        A payment bundles several approved invoices for one vendor into a single ACH. Settle it once,
        attach the bank confirmation report, and both the submitter and the approver are emailed with
        that report attached.
      </p>

      {!payments.length && (
        <div className="border p-12 text-center text-sm" style={{ borderColor: "var(--border)", background: "var(--surface)", color: "var(--muted)" }} data-testid="payment-groups-empty">
          No grouped payments yet. Select approved invoices on the Invoices tab and choose
          &ldquo;Group for payment&rdquo;.
        </div>
      )}

      <div className="space-y-5">
        {payments.map((payment) => (
          <div key={payment.id} className="border" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid={`payment-${payment.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4 p-6 border-b" style={{ borderColor: "var(--border)" }}>
              <div>
                <div className="eyebrow">{payment.company}</div>
                <div className="font-heading font-bold text-2xl mt-1" style={{ color: "var(--ink)" }}>{payment.vendor_name}</div>
                <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                  {payment.invoices.length} invoice{payment.invoices.length === 1 ? "" : "s"} · grouped by {payment.created_by}
                  {payment.ach_reference ? ` · ref ${payment.ach_reference}` : ""}
                  {payment.paid_at ? ` · paid ${payment.paid_at.slice(0, 10)}` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="stat-num text-3xl" data-testid={`payment-total-${payment.id}`}>{money(payment.total)}</div>
                <span className="pill text-[10px] mt-2 inline-block" data-testid={`payment-status-${payment.id}`}
                      style={payment.status === "paid" ? { color: "#1F6F4A" } : { color: "#B45309" }}>
                  {payment.status === "paid" ? "Paid" : "Awaiting payment"}
                </span>
              </div>
            </div>

            <table className="w-full text-sm">
              <tbody>
                {payment.invoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`payment-invoice-${invoice.id}`}>
                    <td className="px-6 py-3 font-mono text-xs" style={{ color: "var(--muted)" }}>{invoice.invoice_number}</td>
                    <td className="px-6 py-3" style={{ color: "var(--muted)" }}>{invoice.invoice_date}</td>
                    <td className="px-6 py-3">
                      {invoice.fund_source !== "ERR" && <span className="pill text-[9px]">{invoice.fund_source}</span>}
                    </td>
                    <td className="px-6 py-3 text-right font-semibold" style={{ color: "var(--ink)" }}>{money(invoice.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex flex-wrap items-center justify-between gap-3 p-6">
              <div className="text-xs flex items-center gap-2" style={{ color: "var(--muted)" }}>
                {payment.confirmation ? (
                  <>
                    <Paperclip size={13} />
                    <button className="underline" onClick={() => window.open(`${api.defaults.baseURL}/attachments/${payment.confirmation.id}/download`, "_blank")} data-testid={`payment-confirmation-${payment.id}`}>
                      {payment.confirmation.filename}
                    </button>
                  </>
                ) : payment.status === "paid" ? (
                  <><FileText size={13} /> Settled without a confirmation report</>
                ) : (
                  <><FileText size={13} /> Attach the bank confirmation when you settle this payment</>
                )}
                {payment.notified?.length ? ` · emailed ${payment.notified.length} recipient(s)` : ""}
              </div>
              {payment.status === "open" && (
                <div className="flex items-center gap-2">
                  <button className="btn-ghost text-xs flex items-center gap-1.5" onClick={() => ungroup(payment)} data-testid={`ungroup-${payment.id}`}>
                    <Trash2 size={13} /> Ungroup
                  </button>
                  <button className="btn-primary text-xs flex items-center gap-1.5" onClick={() => openPay(payment)} data-testid={`pay-${payment.id}`}>
                    <Banknote size={13} /> Mark paid &amp; notify
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={Boolean(target)} onOpenChange={(isOpen) => !isOpen && setTarget(null)}>
        <DialogContent className="!rounded-none max-w-lg" style={{ borderColor: "var(--border)" }} data-testid="pay-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">Mark payment paid</DialogTitle>
            <DialogDescription>
              {target?.vendor_name} · {target?.company} · {money(target?.total || 0)} across {target?.invoices?.length} invoice(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <label className="field-label">Bank confirmation report (PDF)</label>
              <input className="input-flat" type="file" accept="application/pdf"
                     onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                     data-testid="pay-confirmation-file" />
              <p className="mt-2 text-xs" style={{ color: form.file ? "var(--muted)" : "#B45309" }}>
                {form.file
                  ? `${form.file.name} · ${(form.file.size / 1024).toFixed(0)} KB — attached to the notification email`
                  : "Strongly recommended. Without it the email goes out with no proof of payment."}
              </p>
            </div>
            <div>
              <label className="field-label">Bank reference</label>
              <input className="input-flat" value={form.ach_reference}
                     onChange={(e) => setForm({ ...form, ach_reference: e.target.value })}
                     placeholder="e.g. JPM ACH 884213" data-testid="pay-reference" />
            </div>
            <div>
              <label className="field-label">Note to client (optional)</label>
              <textarea className="input-flat resize-none" rows={3} value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        data-testid="pay-note" />
            </div>
          </div>
          <DialogFooter>
            <button type="button" className="btn-ghost" onClick={() => setTarget(null)} data-testid="cancel-pay-payment">Cancel</button>
            <button type="button" className="btn-primary" disabled={saving} onClick={markPaid} data-testid="confirm-pay-payment">
              {saving ? "Sending..." : "Mark paid & notify"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
